import { CanActivateFn, Router } from '@angular/router';
import { Signal, computed, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { REVIEW_BANDS_VIEW } from '../auth/capabilities';
import { CapabilityAccessService } from '../auth/capability-access.service';
import { FeatureFlagService, REVIEW_BANDS_ENABLED_FLAG_KEY } from './feature-flag.service';

/**
 * Gates the Price Band `/pricing/review-bands` tab behind the
 * `review-bands-enabled` Amplitude flag, falling back to the `reviewBands`
 * environment flag when Amplitude has no variant for the caller (or Amplitude
 * is disabled for the env). Mirrors `pricingEnabledGuard`
 * so the tab can roll out per-tenant/per-user via Amplitude without a redeploy;
 * deployed envs fall through to `false` until Amplitude reports `'on'`. The
 * backend endpoints are not separately gated, so flipping this flag is all it
 * takes. Defense-in-depth against deep links; the sidebar entry also checks the
 * flag.
 */
export const reviewBandsEnabledGuard: CanActivateFn = async () => {
  const flags = inject(FeatureFlagService);
  const router = inject(Router);
  await flags.ready();
  return (
    flags.isEnabledWithFallback(REVIEW_BANDS_ENABLED_FLAG_KEY, environment.features.reviewBands) ||
    router.createUrlTree(['/pricing'])
  );
};

/**
 * The Review Bands route's two checks — this flag and `review_bands.view` — for a link into
 * Review Bands from another page, so the link shows only where its route would open (GF-6380).
 */
export function reviewBandsAdmits(): Signal<boolean> {
  const flag = inject(FeatureFlagService).enabledWithFallback(
    REVIEW_BANDS_ENABLED_FLAG_KEY,
    environment.features.reviewBands
  );
  const access = inject(CapabilityAccessService);
  return computed(() => flag() && access.allows(REVIEW_BANDS_VIEW));
}
